import { apiRequest } from "../apiClient";
/**
 * Event SDK
 *
 * The main interface for capturing events in the application.
 * Handles validation, enrichment, queueing, and sync coordination.
 */

import type { CanonicalEvent, EventInput, ConsentScope } from "./types";
import { isCriticalEvent } from "./types";
import { validateEventInput, validateEvent } from "./validation";
import {
  storeEvent,
  getPendingEvents,
  updateEventStatus,
  markEventsSynced,
  manageQuota,
  getStorageStats,
} from "./storage";

// ============================================
// CONFIGURATION
// ============================================

interface EventSDKConfig {
  tenantId: string;
  userId?: string;
  defaultConsentScope: ConsentScope;
  syncEndpoint?: string;
  syncIntervalMs?: number;
  maxRetries?: number;
  enabled?: boolean;
}

const DEFAULT_CONFIG: Partial<EventSDKConfig> = {
  defaultConsentScope: "personal_only",
  syncIntervalMs: 30000, // 30 seconds
  maxRetries: 5,
  enabled: true,
};

// ============================================
// SDK STATE
// ============================================

let config: EventSDKConfig | null = null;
let syncIntervalId: number | null = null;
let isOnline = typeof navigator !== "undefined" ? navigator.onLine : true;
let sessionId: string | null = null;
let inMemoryQueue: CanonicalEvent[] = []; // Fallback when IndexedDB fails

// ============================================
// INITIALIZATION
// ============================================

/**
 * Initialize the Event SDK
 */
export function initializeEventSDK(userConfig: EventSDKConfig): void {
  config = { ...DEFAULT_CONFIG, ...userConfig };
  sessionId = generateUUID();

  // Listen for online/offline status
  if (typeof window !== "undefined") {
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
  }

  // Start sync loop
  if (config.syncEndpoint && config.syncIntervalMs) {
    startSyncLoop();
  }

  console.info("[EventSDK] Initialized", {
    tenantId: config.tenantId,
    enabled: config.enabled,
  });
}

/**
 * Update SDK configuration (e.g., when user logs in)
 */
export function updateEventSDKConfig(updates: Partial<EventSDKConfig>): void {
  if (!config) {
    console.warn("[EventSDK] Cannot update config before initialization");
    return;
  }

  config = { ...config, ...updates };

  // Restart sync loop if interval changed
  if (updates.syncIntervalMs && config.syncEndpoint) {
    stopSyncLoop();
    startSyncLoop();
  }
}

/**
 * Shutdown the Event SDK
 */
export function shutdownEventSDK(): void {
  stopSyncLoop();

  if (typeof window !== "undefined") {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  }

  inMemoryQueue = [];
  config = null;
  sessionId = null;
  console.info("[EventSDK] Shutdown");
}

// ============================================
// EVENT TRACKING
// ============================================

/**
 * Generate a UUID v4
 */
function generateUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Track an event
 * This is the main entry point for event capture
 */
export function activityMetadata(input: object): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const key of [
    "technique_id",
    "module_id",
    "game_id",
    "duration_ms",
    "module_category",
    "score",
  ]) {
    const value = (input as Record<string, unknown>)[key];
    if (typeof value === "string" && value.length <= 100) payload[key] = value;
    if (
      typeof value === "number" &&
      Number.isFinite(value) &&
      value >= 0 &&
      value <= 3600000
    )
      payload[key] = value;
  }
  return payload;
}
export async function trackEvent(input: EventInput): Promise<boolean> {
  if (!config || !config.enabled) {
    return false;
  }

  if (
    ![
      "technique_session_completed",
      "module_completed",
      "ef_game_completed",
    ].includes(input.event_name) ||
    input.consent_scope === "personal_only"
  )
    return false;
  // Validate input
  const validation = validateEventInput(input);
  if (!validation.valid) {
    console.error("[EventSDK] Invalid event input:", validation.errors);
    return false;
  }

  // Create canonical event
  const now = new Date().toISOString();
  const event: CanonicalEvent = {
    idempotency_key: generateUUID(),
    event_name: input.event_name,
    tenant_id: config.tenantId,
    actor_type: input.actor_type || "user",
    user_id: config.userId,
    consent_scope: input.consent_scope || config.defaultConsentScope,
    occurred_at: now,
    queued_at: now,
    payload: activityMetadata(input.payload),
    _local: {
      sync_status: "pending",
      sync_attempts: 0,
    },
  };

  // Validate complete event
  const eventValidation = validateEvent(event);
  if (!eventValidation.valid) {
    console.error(
      "[EventSDK] Event validation failed:",
      eventValidation.errors,
    );
    return false;
  }

  // Store event
  try {
    await storeEvent(event);

    // Trigger quota management asynchronously
    manageQuota().catch((err) => {
      console.warn("[EventSDK] Quota management error:", err);
    });

    // If online and critical event, trigger immediate sync
    if (isOnline && isCriticalEvent(input.event_name)) {
      syncEvents().catch((err) => {
        console.warn("[EventSDK] Critical event sync error:", err);
      });
    }

    return true;
  } catch (err) {
    console.error("[EventSDK] Failed to store event:", err);

    // For critical events, fall back to in-memory queue
    if (isCriticalEvent(input.event_name)) {
      inMemoryQueue.push(event);
      console.info("[EventSDK] Critical event queued in memory");
    }

    return false;
  }
}

// ============================================
// CONVENIENCE TRACKING METHODS
// ============================================

/**
 * Track a page view
 */
export function trackPageView(
  pagePath: string,
  pageTitle?: string,
): Promise<boolean> {
  return trackEvent({
    event_name: "page_viewed",
    payload: {
      page_path: pagePath,
      page_title: pageTitle,
    },
  });
}

/**
 * Track mood check-in
 */
export function trackMoodCheckIn(
  mood: "stressed" | "okay" | "good",
  context?: string,
): Promise<boolean> {
  return trackEvent({
    event_name: "mood_checkin_completed",
    payload: {
      mood,
      context, // Note: context is sensitive and may be filtered in analytics
    },
  });
}

/**
 * Track technique session completion
 */
export function trackTechniqueSession(
  techniqueId: string,
  techniqueName: string,
  durationMs: number,
  stressBefore?: number,
  stressAfter?: number,
): Promise<boolean> {
  return trackEvent({
    event_name: "technique_session_completed",
    payload: {
      technique_id: techniqueId,
      technique_name: techniqueName,
      duration_ms: durationMs,
      stress_before: stressBefore,
      stress_after: stressAfter,
      improvement:
        stressBefore !== undefined && stressAfter !== undefined
          ? stressBefore - stressAfter
          : undefined,
    },
  });
}

/**
 * Track module completion
 */
export function trackModuleCompleted(
  moduleId: string,
  moduleName: string,
  category?: string,
): Promise<boolean> {
  return trackEvent({
    event_name: "module_completed",
    payload: {
      module_id: moduleId,
      module_name: moduleName,
      module_category: category,
    },
  });
}

/**
 * Track crisis detection (CRITICAL EVENT)
 */
export function trackCrisisDetected(
  detectionMethod: "deterministic" | "ai_secondary",
  severity: "low" | "medium" | "high",
  triggerSource: "chat" | "journal" | "reflection" | "feedback" | "other",
): Promise<boolean> {
  return trackEvent({
    event_name: "crisis_detected",
    payload: {
      detection_method: detectionMethod,
      severity,
      trigger_source: triggerSource,
      // Note: actual content is NEVER included
    },
  });
}

/**
 * Track AI coach message
 */
export function trackAICoachMessage(
  conversationId: string,
  messageIndex: number,
  direction: "sent" | "received",
): Promise<boolean> {
  return trackEvent({
    event_name:
      direction === "sent"
        ? "ai_coach_message_sent"
        : "ai_coach_message_received",
    actor_type: direction === "sent" ? "user" : "ai_coach",
    payload: {
      conversation_id: conversationId,
      message_index: messageIndex,
      // Note: message content is NEVER included
    },
  });
}

// ============================================
// SYNC OPERATIONS
// ============================================

function handleOnline(): void {
  isOnline = true;
  console.info("[EventSDK] Online - triggering sync");

  // Flush in-memory queue first
  flushInMemoryQueue();

  // Sync stored events
  syncEvents().catch((err) => {
    console.warn("[EventSDK] Sync error after coming online:", err);
  });
}

function handleOffline(): void {
  isOnline = false;
  console.info("[EventSDK] Offline");
}

/**
 * Flush in-memory queue to IndexedDB or sync directly
 */
async function flushInMemoryQueue(): Promise<void> {
  if (inMemoryQueue.length === 0) return;

  const queue = [...inMemoryQueue];
  inMemoryQueue = [];

  for (const event of queue) {
    try {
      await storeEvent(event);
    } catch (err) {
      console.error("[EventSDK] Failed to flush event from memory:", err);
      // Re-queue on failure
      inMemoryQueue.push(event);
    }
  }
}

/**
 * Sync pending events to server
 */
export async function syncEvents(): Promise<{
  synced: number;
  failed: number;
}> {
  const current = config;
  if (
    !current?.enabled ||
    !current.syncEndpoint ||
    !isOnline ||
    current.defaultConsentScope !== "institution_aggregate_only"
  )
    return { synced: 0, failed: 0 };
  const pending = (await getPendingEvents(50)).filter(
    (e) =>
      e.user_id === current.userId &&
      e.tenant_id === current.tenantId &&
      e.consent_scope === "institution_aggregate_only" &&
      [
        "technique_session_completed",
        "module_completed",
        "ef_game_completed",
      ].includes(e.event_name),
  );
  if (!pending.length || config !== current) return { synced: 0, failed: 0 };
  try {
    const result = await apiRequest<{ synced: string[] }>("/events/ingest", {
      method: "POST",
      body: JSON.stringify({
        events: pending.map((e) => {
          const { _local, ...data } = e;
          void _local;
          return { ...data, payload: activityMetadata(data.payload) };
        }),
      }),
    });
    if (config !== current) return { synced: 0, failed: 0 };
    const keys = Array.isArray(result.synced)
      ? result.synced.filter((k) =>
          pending.some((e) => e.idempotency_key === k),
        )
      : [];
    await markEventsSynced(keys, true);
    return { synced: keys.length, failed: pending.length - keys.length };
  } catch {
    if (config === current)
      for (const e of pending)
        await updateEventStatus(
          e.idempotency_key,
          "pending",
          "Service unavailable; retained for retry",
        );
    return { synced: 0, failed: pending.length };
  }
}

function startSyncLoop(): void {
  if (syncIntervalId || !config?.syncIntervalMs) return;

  syncIntervalId = window.setInterval(() => {
    if (isOnline) {
      syncEvents().catch((err) => {
        console.warn("[EventSDK] Sync loop error:", err);
      });
    }
  }, config.syncIntervalMs);
}

function stopSyncLoop(): void {
  if (syncIntervalId) {
    clearInterval(syncIntervalId);
    syncIntervalId = null;
  }
}

// ============================================
// DEBUGGING & OBSERVABILITY
// ============================================

/**
 * Get SDK status for debugging
 */
export async function getSDKStatus(): Promise<{
  initialized: boolean;
  enabled: boolean;
  online: boolean;
  sessionId: string | null;
  tenantId: string | null;
  storage: Awaited<ReturnType<typeof getStorageStats>>;
  inMemoryQueueSize: number;
}> {
  const storage = await getStorageStats();

  return {
    initialized: config !== null,
    enabled: config?.enabled ?? false,
    online: isOnline,
    sessionId,
    tenantId: config?.tenantId ?? null,
    storage,
    inMemoryQueueSize: inMemoryQueue.length,
  };
}

/**
 * Force an immediate sync (for testing/debugging)
 */
export async function forceSyncNow(): Promise<{
  synced: number;
  failed: number;
}> {
  await flushInMemoryQueue();
  return syncEvents();
}
